/**
 * This browser cannot run the listening loop.
 *
 * Names what is missing, one line each, in terms of what stops working — because the two
 * likely causes are both fixable by the person reading it. "A secure connection" almost
 * always means they typed `http://` at a LAN address; "AudioWorklet" almost always means
 * an iPad that has not been updated in a while. Neither is helped by the phrase
 * "unsupported browser".
 *
 * ## What "unsupported" now means
 *
 * Only the microphone. Every one of these capabilities is about hearing a room, and On the
 * Go needs none of them — no `AudioContext`, no worklet, no secure context beyond what the
 * page already has. So this screen leads with the mode that works here in full rather than
 * with an apology, and the missing-parts list is demoted to what it actually is: an
 * explanation of why listening is off.
 *
 * Storage is the exception, and the only warning left standing. It is the one capability
 * On the Go genuinely depends on, because a sketch that cannot be written down is a
 * session that ends when the tab does.
 */

import { CAPABILITY_INFO, type CapabilityReport } from '../../capabilities.js';
import { ContinueOnTheGo } from './ModeToggle.js';
import { ManualEntryPanel } from './ManualEntry.js';
import type { SessionView } from './sessionView.js';

export interface UnsupportedScreenProps {
  report: CapabilityReport;
  view: SessionView;
}

export function UnsupportedScreen({ report, view }: UnsupportedScreenProps): React.JSX.Element {
  return (
    <section className="hc-screen" data-testid="hc-unsupported">
      <h1 className="hc-screen__title">This browser can’t listen</h1>
      <p className="hc-screen__lead">
        Everything else works. Tap chords instead of playing them and the graph, the ideas and the
        sketchbook are exactly as they would be at a piano.
      </p>
      <ContinueOnTheGo view={view} label="Explore on the go" />
      <p className="hc-screen__footnote">The parts that are missing, and what each one costs:</p>
      <ul className="hc-missing" data-testid="hc-missing-capabilities">
        {report.missing.map((id) => (
          <li key={id} className="hc-missing__item" data-capability={id}>
            <span className="hc-missing__label">{CAPABILITY_INFO[id].label}</span>
            <span className="hc-missing__why">{CAPABILITY_INFO[id].consequence}</span>
          </li>
        ))}
      </ul>
      {report.manualEntryPossible ? <ManualEntryPanel view={view} /> : null}
      {!report.canSave ? (
        <p className="hc-screen__footnote">
          Sketches can’t be saved on this device, so anything you play here is temporary.
        </p>
      ) : null}
    </section>
  );
}
